import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation, Outlet } from 'react-router-dom';
import './App.css';
import HomePage from './components/HomePage';
import CreateProject from './components/CreateProject';
import AllProjects, { FolderLayout } from './components/FolderLayout';
import ProjectDetail from './components/ProjectDetail';
import EstimationDetail from './pages/EstimationDetail';
import SupabaseSync from './components/SupabaseSync';
import LocalStorageMigration from './components/LocalStorageMigration';
import Login from './components/Login';
import { ProjectsProvider, useProjects } from './contexts/ProjectsContext';
import { FoldersProvider, useFolders } from './contexts/FoldersContext';
import { ClientsProvider } from './contexts/ClientsContext';
import { TeamProvider } from './contexts/TeamContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { setupDatabase } from './lib/setupDatabase';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/DarkTheme.css';


// Route wrapper components
function FolderRoute() {
  const { folderId } = useParams();
  const { folders } = useFolders();
  const folder = folders.find(f => f.id === folderId);

  if (!folder) return <div>Folder not found</div>;

  return <FolderLayout folderId={folderId!} folderName={folder.name} />;
}

function ProjectRoute() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  return <ProjectDetail projectId={projectId!} onBack={() => navigate('/projects')} />;
}

function CreateProjectRoute() {
  const navigate = useNavigate();

  return <CreateProject onCancel={() => navigate('/projects')} />;
}

// Main App Content (inside providers)
function AppContent() {
  const { username, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: 'var(--text-muted)'
      }}>
        Loading...
      </div>
    );
  }

  if (!username) {
    return <Login />;
  }

  return (
    <ErrorBoundary>
      <SupabaseSync />
      <LocalStorageMigration />
      <div className="app-container-no-sidebar">
        <main className="main-content-full">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/projects" element={<AllProjects />} />
            <Route path="/create-project" element={<CreateProjectRoute />} />
            <Route path="/folder/:folderId" element={<FolderRoute />} />
            <Route path="/project/:projectId" element={<ProjectRoute />} />
            <Route path="/estimation/:id" element={<EstimationDetail />} />
          </Routes>
        </main>
      </div>
    </ErrorBoundary>
  );
}

function App() {
  console.log('App component rendering...');

  useEffect(() => {
    // Initialize database tables on app startup
    setupDatabase().then(result => {
      if (result.success) {
        console.log('Database setup completed successfully');
      } else {
        console.error('Database setup failed:', result.error);
      }
    });
    
    // Suppress PDF.js rendering cancelled errors (harmless during mode transitions)
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorMsg = error?.message || String(error);
      
      if (errorMsg.includes('Rendering cancelled') || 
          error?.name === 'RenderingCancelledException' ||
          errorMsg.includes('Worker task was terminated') ||
          errorMsg.includes('sendWithPromise') ||
          errorMsg.includes('sendWithStream') ||
          errorMsg.includes('Transport destroyed') ||
          errorMsg.includes('Failed to load')) {
        event.preventDefault(); // Suppress the error
        console.log('🔇 Suppressed PDF error:', errorMsg.substring(0, 50) + '...');
      }
    };
    
    // Also catch regular errors from PDF.js
    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes('sendWithPromise') ||
          event.message?.includes('sendWithStream') ||
          event.message?.includes('Worker task was terminated')) {
        event.preventDefault();
        console.log('🔇 Suppressed PDF.js worker error');
      }
    };
    
    window.addEventListener('error', handleError);
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <FoldersProvider>
            <ClientsProvider>
              <TeamProvider>
                <ProjectsProvider>
                  <AppContent />
                </ProjectsProvider>
              </TeamProvider>
            </ClientsProvider>
          </FoldersProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;